/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerUnion } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { emailValidation } from '@adornis/users/db/validators.js';
import { getUserFromContext } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { min } from '@adornis/validation/functions/range.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { DateTime } from 'luxon';
import type { Document, InsertOneResult } from 'mongodb';
import { Adresse } from './adresse.js';
import { ChancenwerkCounter } from './counters.js';
import {
  LogistikMailTitles,
  LogistikOrderDeliveryStateEnum,
  LogistikProductAvailability,
  LogistikProductBahncard,
  LogistikProductCategory,
  LogistikShippingMethod,
  LogistikShippingRecipient,
  LogistikShippingStatus,
  LogistikTripAge,
} from './enums/LogistikEnums.js';
import { CLEARANCE_FROM_ALL } from './filter/LogistikOrderFilter.js';
import { LogistikSettings } from './LogistikClearanceSettings.js';
import { AdvertisingMaterial, ChancenBrief, LearningMaterial, LogistikProduct } from './LogistikProduct.js';
import {
  digitalDelivery,
  heaufigenBestellungenMail,
  mergeOrderInfoMail,
  newClearanceNeeded,
  newCustomEmail,
  orderApproved,
  orderDeclined,
  orderDeliveryStatusRequest,
  orderStatusUpdateNew,
  orderStatusUpdateProblem,
  orderStatusUpdateProcessing,
  orderStatusUpdateShipped,
  selfPrintDelivery,
} from './mailer/logistikMailTemplates.js';
import { alternativeMailer, chancenWerkMailer } from './mailer/server/mailer.js';
import { KPF } from './master-data-management/kpf/entities/KPF.js';
import { Partner } from './master-data-management/partner/entities/Partner.js';
import { School } from './master-data-management/partner/entities/School.js';
import { ChancenwerkUser } from './master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from './master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from './master-data-management/users/entities/Mitarbeiter.js';
import { ChancenPermission } from './permissions.js';
import { KPFPublicInformationAPI } from './public-access/api/KPFPublicInformationAPI.js';
import { PartnerPublicInformationAPI } from './public-access/api/PartnerPublicInformationAPI.js';
import { ChancenRole } from './semantics/ChancenRole.js';
import { LogistikAccessToken, LogistikSchoolAccessToken } from './tokens.js';

export enum LogistikOrderClearanceState {
  NEEDS_CLEARANCE = 'needs_clearance',
  APPROVED = 'approved',
  DECLINED = 'declined',
}

const LogistikOrderItemUnion = registerUnion({
  name: 'LogistikOrderItemUnion',
  types: () => [LogistikOrderItem, LogistikIndividualOrderItem, LogistikEntireTrip, LogistikOtherOrder],
});

@Entity()
export class LogistikOrderDeliveryState extends AdornisEntity {
  static override _class = 'HubOrderDeliveryState'; // TODO needs migration before we can change this

  @Field(() => String, { default: v => v ?? LogistikOrderDeliveryStateEnum.UNKNOWN })
  status!: LogistikOrderDeliveryStateEnum;

  @Field(() => DateTime)
  updatedAt: Maybe<DateTime>;
}

@Entity()
export class LogistikOrderClearanceAlternative extends AdornisEntity {
  static override _class = 'LogistikOrderClearanceAlternative';

  public static override resolveID(entity: LogistikOrderClearanceAlternative): string {
    return entity.oldLink ?? '';
  }

  @validate(({ value, key, target }) => {
    if (value && !/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}[/]?/.test(value))
      throw new ValidationError('Bitte gib den Link komplett an (z.B.: https://google.com)', { key, target });
  })
  @Field(() => String)
  oldLink?: string;

  @validate(({ value, key, target }) => {
    if (value && !/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}[/]?/.test(value))
      throw new ValidationError('Bitte gib den Link komplett an (z.B.: https://google.com)', { key, target });
  })
  @Field(() => String)
  link?: string;

  @validate(({ value, key, target }) => {
    if (target.link && (value === null || value === undefined))
      throw new ValidationError('Bitte gib einen alternativen Preis ein', { key, target });
  })
  @Field(() => Float)
  price?: number;
}

/**
 * @entity LogistikOrder
 */
@Entity()
export class LogistikOrder extends MongoEntity {
  public static override _class = 'HubOrder'; // TODO needs migration before we can change this
  public static override _collectionName = 'hubOrders'; // TODO needs migration before we can change this

  static override get allFields(): BaseQLSelectionSet<LogistikOrder> {
    return {
      _id: 1,
      _class: 1,
      studentCount: 1,
      createdAt: 1,
      orderNumber: 1,
      splitOrderIDs: 1,
      splitIndex: 1,
      status: 1,
      processorID: 1,
      deliveryStatus: LogistikOrderDeliveryState.allFields,
      shippingMethod: 1,
      trackingLink: 1,
      isManuallyCreated: 1,
      emailsSent: 1,
      processor: { _id: 1, _class: 1, email: 1, username: 1, vorname: 1, nachname: 1 },
      shippingDetails: {
        _class: 1,
        dueDate: 1,
        customerEmail: 1,
        shippingRecipient: 1,
        shippingRecipientName: 1,
        shippingRecipientzuHaenden: 1,
        shippingSchoolID: 1,
        isEmailKnown: 1,
        shippingInstructions: 1,
        shippingAddress: Adresse.allFields,
        shippingSchool: {
          _id: 1,
          _class: 1,
          name: 1,
          kuerzel: 1,
          lastAdvertisingMaterialOrderDate: 1,
          lastLearningMaterialOrderDate: 1,
          zipCity: { zip: 1, city: 1, land: 1 },
          address: Adresse.allFields,
          strasse: 1,
          country: 1,
          standort: 1,
        },
        kpfID: 1,
        // @ts-expect-error inifinite depth
        kpf: { produkt: { kuerzel: 1 } },
      },
      splitFrom: 1,
      clearanceFrom: 1,
      clearanceNote: 1,
      clearanceState: 1,
      clearanceAlternatives: LogistikOrderClearanceAlternative.allFields,
      items: {
        ...LogistikOrderItem.allFields,
        ...LogistikOrderChancenBriefItem.allFields,
        ...LogistikLearningMaterialOrderItem.allFields,
        ...LogistikIndividualOrderItem.allFields,
        ...LogistikEntireTrip.allFields,
        ...LogistikOtherOrder.allFields,
      },
      checkedItems: 1,
      notes: 1,
      isExpress: 1,
      accessToken: 1,
      accessTokenValid: 1,
    } as const;
  }

  static get allFieldsWithOriginalOrders(): BaseQLSelectionSet<LogistikOrder> {
    return { ...LogistikOrder.allFields, mergeOriginalOrders: LogistikOrder.allFields };
  }

  static override async afterCreate(entity: LogistikOrder, result: InsertOneResult<Document>): Promise<void> {
    await LogistikOrder.sendStatusUpdateMail(entity)();
    await LogistikOrder.sendClearanceNeededMail(entity);
    await LogistikOrder.checkHaeufigenBestellungen(entity);
    await LogistikOrder.updateSchoolOrderDates(entity);
    await LogistikOrder.deliverDigitalLearningMaterial(entity);
  }

  private static async updateSchoolOrderDates(entity: LogistikOrder) {
    const school = await School.getByID<School>(entity.shippingDetails.shippingSchoolID)(School.allFields);
    if (school) {
      if (await entity.containsAdvertisingMaterials()) {
        school.lastAdvertisingMaterialOrderDate = DateTime.now();
      }
      if (await entity.containsLearningMaterials()) {
        school.lastLearningMaterialOrderDate = DateTime.now();
      }
      await school.save();
    }
  }

  private static async deliverDigitalLearningMaterial(order: LogistikOrder) {
    const recipient = new Recipient(order.shippingDetails.customerEmail);
    const promises = order.items
      .filter(item => item instanceof LogistikLearningMaterialOrderItem && item.isDigital)
      .map(async item => {
        const product = await getByID<typeof LearningMaterial>(
          LearningMaterial,
          (item as LogistikLearningMaterialOrderItem).productID,
        )({ digitalVariantPDFID: 1, title: 1 });
        if (!(product instanceof LearningMaterial && product.digitalVariantPDFID)) return false;

        try {
          await chancenWerkMailer.sendMail(
            await Message.compose(digitalDelivery({ productTitle: product.title, pdfID: product.digitalVariantPDFID })),
            recipient,
          );
          return true;
        } catch (err) {
          logger.error({ err }, 'Fehler beim Versenden einer digitalen Bestellung');
          return false;
        }
      });

    const results = await Promise.all(promises);
    if (results.every(r => r) && results.length === order.items.length) {
      order.shippingMethod = LogistikShippingMethod.EMAIL;
      order.deliveryStatus = new LogistikOrderDeliveryState({
        status: LogistikOrderDeliveryStateEnum.RECEIVED,
        updatedAt: DateTime.now(),
      });
      order.status = LogistikShippingStatus.COMPLETED;
      await order.save();
    }
  }

  private static async deliverSelfPrintChancenBrief(entity: LogistikOrder) {
    const order = await (await getCollection<LogistikOrder>(LogistikOrder._class)).findOne({ _id: entity._id });
    if (!order) return;
    const recipient = new Recipient(order.shippingDetails.customerEmail);
    const promises = order.items
      .filter(
        (item: any): item is EntityData<LogistikOrderChancenBriefItem> =>
          item._class === LogistikOrderChancenBriefItem._class && item.isSelfPrint,
      )
      .map(i => constructValue<LogistikOrderChancenBriefItem>(i))
      .map(async item => {
        if (!item.flyaVariableValues) return false;
        const product = await getByID(ChancenBrief, item.productID)({ title: 1 });
        if (!product) return false;

        const { path, searchParams } = product.getGeneratedPDFLink(item.flyaVariableValues!);
        const success = await Message.compose(
          selfPrintDelivery({ productTitle: product.title, generatedLink: A.absoluteUrl(path, searchParams) }),
        )
          .then(msg => chancenWerkMailer.sendMail(msg, recipient))
          .then(() => true)
          .catch(err => {
            logger.error({ err }, 'Fehler beim Versenden eines Selbstdruck-ChancenBriefes');
            return false;
          });
        return success;
      });

    const results = await Promise.all(promises);
    if (results.every(r => r) && results.length === order.items.length) {
      order.shippingMethod = LogistikShippingMethod.EMAIL;
      order.deliveryStatus = new LogistikOrderDeliveryState({
        status: LogistikOrderDeliveryStateEnum.RECEIVED,
        updatedAt: DateTime.now(),
      });
      order.status = LogistikShippingStatus.COMPLETED;
      const constructedOrder = constructValue<LogistikOrder>(order);
      await constructedOrder.save();
    }
  }

  static override async transformBeforeUpdate(
    oldEntity: EntityData<LogistikOrder>,
    update: LogistikOrder,
  ): Promise<void> {
    // delivery status update
    if (update.deliveryStatus && oldEntity.deliveryStatus?.status !== update.deliveryStatus.status)
      update.deliveryStatus.updatedAt = DateTime.now();
    update.updatedAt = new Date();

    return super.transformBeforeUpdate(oldEntity, update);
  }

  static override async afterUpdate(oldEntity: LogistikOrder, newEntity: LogistikOrder) {
    // shipping status update
    if (oldEntity.status !== newEntity.status) {
      void LogistikOrder.sendStatusUpdateMail(newEntity)().catch(e =>
        logger.warn({ err: e }, 'There was a problem sending a status update mail'),
      );
      if (newEntity.status === LogistikShippingStatus.SHIPPED) {
        // adjust availableQuantity of products
        await Promise.all(
          newEntity.items.map(async item => {
            if (!(item instanceof LogistikOrderItem)) return;
            const product = await getByID(
              LogistikProduct,
              item.productID,
            )({
              _id: 1,
              _class: 1,
              availableQuantity: 1,
              quantityWarningThreshold: 1,
              minOrderAmount: 1,
              availability: 1,
            });
            if (product) {
              product.availableQuantity = Math.max(0, product.availableQuantity - item.quantity);

              // check if availableQuantity is 0 or below
              if (product.availableQuantity <= (product.minOrderAmount || 0)) {
                product.availability = LogistikProductAvailability.UNAVAILABLE;
              }
              await product.save();

              // check if availableQuantity is below threshold. Only after the save: the mail re-reads
              // the product from the DB, so firing it earlier reports the stock from before this order.
              if (product.availableQuantity < product.quantityWarningThreshold) {
                void LogistikProduct.sendQuantityWarningMail(product._id)().catch(err =>
                  logger.warn({ err }, 'There was a problem sending a quantity warning mail'),
                );
              }
            }
          }),
        );
      }
    }

    // check clearance state
    if (newEntity.has('clearanceState') && oldEntity.clearanceState !== newEntity.clearanceState) {
      if (newEntity.clearanceState === LogistikOrderClearanceState.APPROVED) {
        LogistikOrder.sendOrderApprovalMail(newEntity);
        LogistikOrder.deliverSelfPrintChancenBrief(newEntity);
      }
      if (newEntity.clearanceState === LogistikOrderClearanceState.DECLINED)
        LogistikOrder.sendOrderDeclinedMail(newEntity);
    }
  }

  static override async beforeCreate(order: LogistikOrder) {
    order.sourceService = projectConfig.get('SERVICE_NAME');
    await LogistikOrder._setOrderClearance(order);

    if (!order.has('orderNumber') || !order.orderNumber)
      order.orderNumber = await ChancenwerkCounter.getIncrementingLogistikOrderNumber()();

    if (order.has('accessToken') && order.accessToken) {
      // Get token maxOrderAmount
      const { maxOrderQuantity } = await LogistikAccessToken.getByToken(+order.accessToken)({ maxOrderQuantity: 1 });

      const totalOrderQuantity = order.items.reduce((acc, item) => {
        if (item instanceof LogistikIndividualOrderItem) return acc;
        if (item instanceof LogistikEntireTrip) return acc;
        if (item instanceof LogistikOtherOrder) return acc;
        return acc + item.quantity;
      }, 0);

      if (typeof maxOrderQuantity === 'number' && totalOrderQuantity > maxOrderQuantity!)
        throw new Error(`Die Bestellung ist zu groß. Maximal ${maxOrderQuantity} Artikel erlaubt.`);
    }

    return super.beforeCreate(order);
  }

  static async _setOrderClearance(order: LogistikOrder) {
    const hasIndividual = order.items.some(
      item => item instanceof LogistikIndividualOrderItem || item._class === LogistikIndividualOrderItem._class,
    );
    const hasSelfprint = order.items.some(
      item =>
        (item instanceof LogistikOrderChancenBriefItem || item._class === LogistikOrderChancenBriefItem._class) &&
        (item as LogistikOrderChancenBriefItem).isSelfPrint,
    );
    if (!hasIndividual && !hasSelfprint) {
      return;
    }

    if (hasIndividual) {
      // ! Due to the way individual order items are placed we do not have to consider
      // ! an order with both individual order items and self print items.
      const clearanceSettings = await LogistikSettings.get()({ clearanceEntries: { price: 1, email: 1 } });
      if (clearanceSettings.clearanceEntries.length > 0) {
        const price = order.items
          .filter(
            item => item instanceof LogistikIndividualOrderItem || item._class === LogistikIndividualOrderItem._class,
          )
          .map(i => i as LogistikIndividualOrderItem)
          .reduce((acc, item) => acc + item.price * (item.quantity ?? 1), 0);
        if (price > 0) {
          const byPrice = clearanceSettings.clearanceEntries
            .sort((a, b) => b.price - a.price)
            .filter(entry => entry.price <= price);
          if (byPrice.length > 0) {
            order.clearanceState = LogistikOrderClearanceState.NEEDS_CLEARANCE;
            order.clearanceFrom = byPrice.map(entry => entry.email);
          }
        }
      }
    } else if (hasSelfprint) {
      order.clearanceState = LogistikOrderClearanceState.NEEDS_CLEARANCE;
      order.clearanceFrom = [CLEARANCE_FROM_ALL];
    }
  }

  @Field(() => DateTime, { default: v => v ?? DateTime.local() })
  public createdAt!: DateTime;

  @validate(({ value, key, source }) => {
    if (value !== '' && value.length !== 8)
      throw new ValidationError(`Bestellnummer muss 8-stellig sein`, { key, source });
    if (value !== '' && !/^\d+$/.test(value))
      throw new ValidationError(`Bestellnummer darf nur aus Zahlen bestehen`, { key, source });
  })
  @Field(() => String, { default: v => v ?? '' })
  public orderNumber!: string;

  @Field(() => String, { default: v => v ?? LogistikShippingStatus.NEW })
  public status!: LogistikShippingStatus;

  @Field(() => String)
  public shippingMethod: Maybe<LogistikShippingMethod>;

  @Field(() => LogistikOrderShippingDetails)
  public shippingDetails!: LogistikOrderShippingDetails;

  @Field(() => [LogistikOrderItemUnion])
  public items!: Array<LogistikIndividualOrderItem | LogistikEntireTrip | LogistikOtherOrder | LogistikOrderItem>;

  @Field(() => [ID], { default: v => v ?? [] })
  checkedItems!: string[];

  @DataLoadedField(() => ChancenwerkUser, ({ processorID }) => processorID)
  public processor!: ChancenwerkUser;

  @Field(() => String)
  public processorID: Maybe<string>;

  @Field(() => String)
  public trackingLink!: string;

  @Field(() => LogistikOrderDeliveryState, { default: v => v ?? new LogistikOrderDeliveryState({}) })
  public deliveryStatus: Maybe<LogistikOrderDeliveryState>;

  @Field(() => String, { default: v => v ?? null })
  clearanceState: Maybe<LogistikOrderClearanceState>;

  @Field(() => [String])
  clearanceFrom?: string[];

  @Field(() => String)
  clearanceNote?: string;

  @Field(() => [LogistikOrderClearanceAlternative])
  clearanceAlternatives?: LogistikOrderClearanceAlternative[];

  @Field(() => [String])
  splitOrderIDs?: string[];

  @Field(() => String)
  splitFrom?: string;

  @Field(() => Int)
  splitIndex?: number;

  @Field(() => [LogistikOrder])
  mergeOriginalOrders?: Maybe<LogistikOrder[]>;

  @Field(() => String)
  notes: Maybe<string>;

  @Field(() => Boolean, { default: v => v ?? false })
  isExpress: Maybe<boolean>;

  @Field(() => Int)
  accessToken: Maybe<number>;

  @Field(() => Boolean, {
    resolve: function (this: LogistikOrder) {
      return async () => {
        const constructed: LogistikOrder = constructValue(this);
        return constructed.accessToken
          ? LogistikSchoolAccessToken.checkCode(constructed.accessToken, constructed.createdAt)
          : false;
      };
    },
  })
  accessTokenValid: Maybe<boolean>;

  /** is true if order was created by admin, doesn't send any notification mails */
  @Field(() => Boolean, { default: v => v ?? false })
  isManuallyCreated!: boolean;

  @validate(({ key, value, target }) => {
    if (projectConfig.get('SERVICE_NAME').toLowerCase() !== 'chancenhefte') return;
    nonOptional({ translationKey: 'Bitte wählen Sie die Anzahl der Schüler:innen' })({ key, value, target });
    min(1, 'Mindest Angenommene Schüler:innen-Anzahl wurde nicht erreicht')({ key, value, target });
  })
  @Field(() => Int)
  studentCount?: number;

  @Field(() => String)
  sourceService?: string;

  @Field(() => Int, { default: v => v ?? 0 })
  public emailsSent: Maybe<number>;

  @Field(() => Date)
  public updatedAt: Maybe<Date>;

  async containsLearningMaterials() {
    for (const item of this.items) {
      if (!(item instanceof LogistikOrderItem)) continue;
      const productID = item.productID;
      const product = await getByID(LogistikProduct, productID)({});
      if (product && product._class === LearningMaterial._class) return true;
    }
    return false;
  }

  async containsAdvertisingMaterials() {
    for (const item of this.items) {
      if (!(item instanceof LogistikOrderItem)) continue;
      const productID = item.productID;
      const product = await getByID(LogistikProduct, productID)({});
      if (product && product._class === AdvertisingMaterial._class) return true;
    }
    return false;
  }

  @Mutation(() => String)
  static splitOrder(
    @Arg('orderID', () => ID) orderID: string,
    @Arg('items', () => [LogistikOrderItem]) items: LogistikOrderItem[],
    @Arg('oldOrderProcessorID', () => ID) oldOrderProcessorID: string,
    @Arg('newOrderProcessorID', () => ID) newOrderProcessorID: string,
  ) {
    return async (gqlFields: void) => {
      const order = await getByID(LogistikOrder, orderID)(LogistikOrder.allFields);
      if (!order) throw new Error('Order not found');

      if (items.some(item => item instanceof LogistikIndividualOrderItem))
        throw new Error('I cant handle individual order items yet');
      if (order.items.some(item => item instanceof LogistikIndividualOrderItem))
        throw new Error('I cant handle individual order items yet');

      // check if all items are in order
      const itemIDs = items.map(item => item.productID);
      const orderItemIDs = (order.items as LogistikOrderItem[]).map(item => item.productID);
      if (!itemIDs.every(id => orderItemIDs.includes(id))) throw new Error('Invalid items');

      // check if the quantities do not exceed the order quantities
      const orderItemQuantities = order.items.reduce((acc, item) => {
        if (!(item instanceof LogistikOrderItem))
          throw new Error('If you see this: fix the types here. We have a Union but no real checking for an instance');
        acc[(item as LogistikOrderItem).productID] = item.quantity;
        return acc;
      }, {});
      const itemQuantities = items.reduce((acc, item) => {
        acc[item.productID] = item.quantity;
        return acc;
      }, {});
      if (!Object.keys(itemQuantities).every(id => itemQuantities[id] <= orderItemQuantities[id]))
        throw new Error('Invalid quantities');

      // create new order
      const splitOrder = new LogistikOrder({
        createdAt: order.createdAt,
        orderNumber: order.orderNumber,
        status: LogistikShippingStatus.NEW,
        shippingDetails: order.shippingDetails,
        items,
        splitFrom: order.splitFrom ? order.splitFrom : order._id,
        processorID: newOrderProcessorID ?? order.processorID,
        splitIndex: order.splitOrderIDs?.length ?? 0,
      });

      // update quantities of items in original order
      order.items = order.items.map(item => {
        if (!(item instanceof LogistikOrderItem)) return undefined;
        if (itemIDs.includes((item as LogistikOrderItem).productID)) {
          item.quantity -= itemQuantities[(item as LogistikOrderItem).productID];
          if (item.quantity === 0) return undefined;
        }
        return item;
      }) as LogistikOrderItem[];
      order.processorID = oldOrderProcessorID;

      let originalOrder: NarrowedEntity<
        LogistikOrder,
        {
          splitOrderIDs: 1;
        }
      > | null;

      // set the splitIndex
      if (order.splitFrom) {
        originalOrder = await getByID(
          LogistikOrder,
          order.splitFrom,
        )({
          splitOrderIDs: 1,
        });
        if (!originalOrder) throw new Error('Original order not found');
        splitOrder.splitIndex = originalOrder.splitOrderIDs?.length ?? 0;
      }

      const newOrderID = await splitOrder.create();

      // check if order is a split order
      if (order.splitFrom) {
        originalOrder ??= await getByID(
          LogistikOrder,
          order.splitFrom,
        )({
          splitOrderIDs: 1,
        });
        if (!originalOrder) throw new Error('Original order not found');
        originalOrder.splitOrderIDs = [...(originalOrder.splitOrderIDs ?? []), newOrderID];
        await originalOrder.save();
      } else {
        order.splitOrderIDs = [...(order.splitOrderIDs ?? []), newOrderID];
      }

      await order.save();
      return newOrderID;
    };
  }

  @Query(() => Boolean)
  public static sendStatusUpdateMail(@Arg('order', () => LogistikOrder) order: LogistikOrder) {
    return async (gqlFields: void) => {
      if (order.isManuallyCreated) return; // we dont send any mails for manually created orders

      let mailTemplate: Renderable;
      switch (order.status) {
        case LogistikShippingStatus.PROBLEM:
          mailTemplate = orderStatusUpdateProblem({ order });
          break;
        case LogistikShippingStatus.PROCESSING:
          mailTemplate = orderStatusUpdateProcessing({ order });
          break;
        case LogistikShippingStatus.SHIPPED:
          mailTemplate = orderStatusUpdateShipped({ order });
          break;
        case LogistikShippingStatus.NEW:
          mailTemplate = orderStatusUpdateNew({ order });
          break;
        case LogistikShippingStatus.COMPLETED:
          return false; // no status email when an order is marked as completed.
        default:
          return false;
      }
      try {
        // this is used to get reverse mapping of enum values, so we can use a enum as mail title
        const keys = Object.keys(LogistikShippingStatus).filter(x => LogistikShippingStatus[x] === order.status);
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: keys[0] ? LogistikMailTitles[keys[0]] : 'Bestellstatus aktualisiert',
            html: mailTemplate,
          }),
          new Recipient(order.shippingDetails.customerEmail),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Query(() => Boolean)
  public static sendCustomEmailToCustomer(
    @Arg('order', () => LogistikOrder) order: LogistikOrder,
    @Arg('subject', () => String) subject: string,
    @Arg('content', () => String) content: string,
    @Arg('useAlternativeMailer', () => Boolean) useAlternativeMailer: boolean = false,
  ) {
    return async (gqlFields: void) => {
      if (
        !(await getUserFromContext<ChancenwerkUser>({ roles: ChancenRole.allFields })
          .then(u => u?.hasPermission(ChancenPermission.logistik.LOGIN_LOGISTIK))
          .catch(() => false))
      )
        // User not authorized to send emails.
        return;
      const email = order.shippingDetails.customerEmail;
      if (!email) return false;
      try {
        await (useAlternativeMailer ? alternativeMailer : chancenWerkMailer).sendMail(
          await Message.compose({ subject, html: newCustomEmail({ title: subject, content }) }),
          new Recipient(email),
        );
        order.emailsSent = (order.emailsSent ?? 0) + 1;
        await order.save();
      } catch (err) {
        logger.error({ err }, 'Fehler beim Senden der Email');
        return false;
      }

      return true;
    };
  }

  @Query(() => Boolean)
  public static sendDeliveryStatusRequestMail(@Arg('order', () => LogistikOrder) order: LogistikOrder) {
    return async (gqlFields: void) => {
      await chancenWerkMailer.sendMail(
        await Message.compose({
          subject: 'Haben Sie Ihre Bestellung erhalten?',
          html: orderDeliveryStatusRequest({ order }),
        }),
        new Recipient(order.shippingDetails.customerEmail),
      );

      if (!order.deliveryStatus) order.deliveryStatus = new LogistikOrderDeliveryState({});
      order.deliveryStatus.status = LogistikOrderDeliveryStateEnum.REQUESTED;
      order.deliveryStatus.updatedAt = DateTime.now();
      await order.save();

      return true;
    };
  }

  @Query(() => Boolean)
  public static setDeliveryStatus(
    @Arg('order', () => LogistikOrder) order: LogistikOrder,
    @Arg('status', () => String) status: LogistikOrderDeliveryStateEnum,
  ) {
    return async (gqlFields: void) => {
      if (!order.deliveryStatus) order.deliveryStatus = new LogistikOrderDeliveryState({});
      order.deliveryStatus.status = status;
      order.deliveryStatus.updatedAt = DateTime.now();
      if (status === LogistikOrderDeliveryStateEnum.RECEIVED) order.status = LogistikShippingStatus.COMPLETED;
      await order.save();
      return true;
    };
  }

  static async generateHefteOrderExportCSV() {
    // TODO check if user has permission to export
    const orderCollection = await getRawCollection<EntityData<LogistikOrder>>(LogistikOrder._collectionName);
    const productCollection = await getRawCollection<EntityData<LogistikProduct>>(LogistikProduct._collectionName);
    const products = await productCollection
      .find({}, { projection: { _id: 1, title: 1, category: 1 }, sort: { _class: 1 } })
      .toArray();
    const makeProductHeader = (product: EntityData<LogistikProduct & { category?: string }>) =>
      product.category ? `${product.category}_${product.title.replace(';', '-')}` : product.title.replace(';', '-');
    const header =
      'Auftragsnummer;Tag;Monat;Jahr;Schulkürzel;KPFKürzel;Status;Förderpartner;' +
      products.map(makeProductHeader).join(';');
    const bodyArray: string[] = [];
    const aggregation = orderCollection.aggregate<
      Omit<EntityData<LogistikOrder>, 'items'> & {
        day: number;
        month: number;
        year: number;
        partner: EntityData<Partner>;
        items: Array<EntityData<LogistikProduct> & { orderQuantity: number }>;
        kpf: Array<EntityData<KPF>>;
        token?: { foundation?: { kuerzel: string } };
      }
    >([
      // Start attach foundation information via access token
      {
        $lookup: {
          from: LogistikSchoolAccessToken._collectionName,
          localField: 'accessToken',
          foreignField: 'code',
          as: 'token',
          pipeline: [
            { $match: { foundationID: { $exists: true } } },
            {
              $lookup: {
                from: 'partners',
                localField: 'foundationID',
                foreignField: '_id',
                as: 'foundation',
                pipeline: [{ $project: { kuerzel: 1 } }],
              },
            },
            { $unwind: { path: '$foundation', preserveNullAndEmptyArrays: true } },
            { $project: { foundation: 1 } },
          ],
        },
      },
      { $unwind: { path: '$token', preserveNullAndEmptyArrays: true } },
      // End Attach foundation information via access token
      {
        $lookup: {
          from: Partner._collectionName,
          localField: 'shippingDetails.shippingSchoolID',
          foreignField: '_id',
          as: 'partner',
        },
      },
      { $unwind: '$partner' },
      // dont need to resolve schoolID from kpf, because its identical to the shippingSchoolID of the order
      { $lookup: { from: KPF._collectionName, localField: 'shippingDetails.kpfID', foreignField: '_id', as: 'kpf' } },
      // dont unwind kpfs as this omits all docs without a kpf
      { $unwind: '$items' },
      {
        $lookup: {
          from: LogistikProduct._collectionName,
          localField: 'items.productID',
          foreignField: '_id',
          as: 'itemSrc',
        },
      },
      { $unwind: '$itemSrc' },
      {
        $group: {
          _id: { $mergeObjects: ['$$ROOT', { items: '', itemSrc: '' }] },
          items: { $push: { $mergeObjects: ['$itemSrc', { orderQuantity: '$items.quantity' }] } },
        },
      },
      { $replaceRoot: { newRoot: { $mergeObjects: ['$_id', { items: '$items' }] } } },
      {
        $addFields: {
          month: { $month: '$createdAt' },
          day: { $dayOfMonth: '$createdAt' },
          year: { $year: '$createdAt' },
        },
      },
      { $project: { itemSrc: 0 } },
    ]);
    for await (const entry of aggregation) {
      // if there is a kpf there always is only one
      const kpf = entry.kpf[0];
      const lineArray = [
        displayLogistikOrderID(entry as unknown as LogistikOrder),
        entry.day,
        entry.month,
        entry.year,
        entry.partner.kuerzel,
        kpf ? `${entry.partner.kuerzel}_${kpf.produkt?._class.slice(7) ?? '???'}` : 'nicht angegeben',
        entry.status,
        entry.token?.foundation?.kuerzel ?? '-',
      ];
      for (const product of products) {
        lineArray.push(entry.items.find(item => item._id === product._id)?.orderQuantity ?? 0);
      }
      bodyArray.push(lineArray.join(';'));
    }

    return `${header}\n${bodyArray.join('\n')}`;
  }

  @Mutation(() => Boolean)
  static mergeOrder(@Arg('newOrder', () => LogistikOrder) newOrder: LogistikOrder) {
    return async () => {
      logger.info({ newOrder }, 'HELLO WHAT IS DIS?');
      if (context.serverContext || !context.userID)
        throw new Error('Zusammengefasste Bestellungen können nur eingeloggt erstellt werden');
      if (!newOrder.mergeOriginalOrders?.length)
        throw new Error('Die übergebene Bestellung ist keine zusammengefasste Bestellung');
      if (newOrder._id) throw new Error('Diese zusammengefasste Bestellung existiert bereits!');

      const rawCol = await getRawCollection<LogistikOrder>(this._collectionName);
      const newOrderID = await newOrder.create();
      await rawCol.deleteMany({ _id: { $in: newOrder.mergeOriginalOrders.map(order => order._id) } });

      await this._sendMergeOrderInfoMail(newOrderID);

      return true;
    };
  }

  private static async _sendMergeOrderInfoMail(newOrderID: string) {
    const newOrder = await getByID(LogistikOrder, newOrderID)(LogistikOrder.allFieldsWithOriginalOrders);
    if (!newOrder) throw new Error('Zusammengefasste Bestellung wurde nicht gefunden');

    if (context.serverContext || !context.userID)
      throw new Error('Zusammengefasste Bestellungen können nur eingeloggt erstellt werden');
    if (!newOrder.mergeOriginalOrders?.length)
      throw new Error('Die übergebene Bestellung ist keine zusammengefasste Bestellung');

    await chancenWerkMailer
      .sendMail(
        await Message.compose({
          subject: 'Bestellung wurde zusammengelegt',
          html: await mergeOrderInfoMail(newOrder as unknown as LogistikOrder),
        }),
        newOrder.mergeOriginalOrders.map(originalOrder => new Recipient(originalOrder.shippingDetails.customerEmail)),
      )
      .catch(err => logger.error({ err }, 'Error while sending merge order info mail'));
  }

  static async sendClearanceNeededMail(order: LogistikOrder) {
    if (
      order.clearanceState !== LogistikOrderClearanceState.NEEDS_CLEARANCE ||
      !order.clearanceFrom ||
      !order.clearanceFrom.length
    ) {
      return;
    }
    const message = await Message.compose({ html: newClearanceNeeded(), subject: 'Neue Freigabe erwartet' });
    const recipient = await (async () => {
      if (!order.clearanceFrom) {
        return undefined;
      }
      const settings = await LogistikSettings.get()({ selfPrintNotificationEmails: 1 });
      const emails = order.clearanceFrom.flatMap(c =>
        c === CLEARANCE_FROM_ALL && settings.selfPrintNotificationEmails ? settings.selfPrintNotificationEmails : [c],
      );
      return new Recipient(emails);
    })();
    await chancenWerkMailer
      .sendMail(message, recipient)
      .catch(err => logger.error({ err }, 'Error while sending clearance needed mail'));
  }

  static async sendOrderApprovalMail(order: LogistikOrder) {
    if (order.isManuallyCreated) return; // we dont send any mails for manually created orders

    await chancenWerkMailer
      .sendMail(
        await Message.compose({ html: orderApproved(), subject: 'Deine individuelle Bestellung wurde freigegeben' }),
        new Recipient(order.shippingDetails.customerEmail),
      )
      .catch(err => logger.error({ err }, 'Error while sending order approved mail'));
  }

  static async sendOrderDeclinedMail(order: LogistikOrder) {
    if (order.isManuallyCreated) return; // we dont send any mails for manually created orders

    await chancenWerkMailer
      .sendMail(
        await Message.compose({ html: orderDeclined(order), subject: 'Deine individuelle Bestellung wurde abgelehnt' }),
        new Recipient(order.shippingDetails.customerEmail),
      )
      .catch(err => logger.error({ err }, 'Error while sending order declined mail'));
  }

  static async checkHaeufigenBestellungen(order: LogistikOrder) {
    const settings = await LogistikSettings.get()({ frequentOrdersNotificationEmails: 1 });
    if (!settings.frequentOrdersNotificationEmails) return;
    try {
      await Promise.all(
        order.items.map(async item => {
          if (item instanceof LogistikOrderItem) {
            const maxOrders = 1;
            const count = await LogistikOrder.countProductOrders(order.shippingDetails.customerEmail, item.productID);
            if (count > maxOrders) {
              const product = await getByID(LogistikProduct, item.productID)({ title: 1 });
              if (product) {
                await chancenWerkMailer.sendMail(
                  await Message.compose({
                    subject: 'Meldung wegen häufiger Bestellungen',
                    html: heaufigenBestellungenMail({
                      product,
                      count,
                      customerEmail: order.shippingDetails.customerEmail,
                    }),
                  }),
                  new Recipient(settings.frequentOrdersNotificationEmails ?? []),
                );
              }
            }
          }
        }),
      );
    } catch (e) {
      logger.error({ err: e }, 'Error while checking for frequent orders');
    }
  }

  private static async countProductOrders(customerEmail: string, productID: string) {
    const collection = await getRawCollection<LogistikOrder>(LogistikOrder._class);
    const orders = await collection
      .aggregate<{ quantity: number }>([
        {
          $match: {
            'shippingDetails.customerEmail': customerEmail,
            createdAt: { $gte: DateTime.local().minus({ week: 8 }).toJSDate() },
          },
        },
        { $unwind: '$items' },
        { $match: { 'items.productID': productID } },
        { $group: { _id: null, quantity: { $sum: '$items.quantity' } } },
      ])
      .toArray();
    return orders.at(0)?.quantity ?? 0;
  }
}

/**
 * @entity LogistikOrderItem
 */
@Entity()
export class LogistikOrderItem extends AdornisEntity {
  public static override _class = 'HubOrderItem'; // TODO needs migration before we can change this

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @DataLoadedField(() => LogistikProduct, ({ productID }) => productID)
  public product!: LogistikProduct;

  @Field(() => String)
  public productID!: string;

  @Field(() => Int)
  public quantity!: number;

  @Field(() => String)
  public productCategory!: LogistikProductCategory;
}

@Entity()
export class LogistikWorkshopPackOrderItem extends LogistikOrderItem {
  static override _class = 'LogistikWorkshopPackOrderItem';

  @validate(nonOptional())
  @Field(() => Int)
  public participantsQuantity!: number;

  @validate(nonOptional())
  @Field(() => Int)
  public trainersQuantity!: number;
}

@Entity()
export class LogistikLearningMaterialOrderItem extends LogistikOrderItem {
  static override _class = 'LogistikLearningMaterialOrderItem';

  @Field(() => Boolean, { default: v => v ?? false })
  public isDigital?: boolean;
}

// ! Do not remove. ProductSetOrderItem are unrolled to other LogistikOrderItem types later on.
@Entity()
export class ProductSetOrderItem extends LogistikOrderItem {
  static override _class = 'ProductSetOrderItem';
}

@Entity()
export class ChancenBriefVariableValue extends AdornisEntity {
  static override _class = 'FLYAVariableValue'; // TODO need to add migration to update this

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(nonOptional())
  @Field(() => String)
  variableName!: string;

  @validate(nonOptional())
  @Field(() => String)
  variableValue!: string;
}

@Entity()
export class LogistikOrderChancenBriefItem extends LogistikOrderItem {
  static override _class = 'HubOrderFLYAItem'; // TODO needs migration before we can change this

  @Field(() => [ChancenBriefVariableValue], { default: v => v ?? [] })
  public flyaVariableValues: Maybe<ChancenBriefVariableValue[]>;

  @Field(() => Boolean, { default: v => v ?? false })
  public isSelfPrint?: boolean;
}

@Entity()
export class LogistikIndividualOrderItem extends AdornisEntity {
  static override _class = 'HubIndividualOrderItem'; // TODO needs migration before we can change this

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(({ value, key, target }) => {
    if (!value) throw new ValidationError(`Bitte gib eine Anzahl an`, { key, target });
  })
  @Field(() => Int)
  quantity!: number;

  @validate(({ value, key, target }) => {
    if (!value) throw new ValidationError(`Bitte gib einen Namen an`, { key, target });
  })
  @Field(() => String)
  name!: string;

  @Field(() => String) type?: string;

  @validate(({ value, key, target }) => {
    if (value === null || value === undefined) throw new ValidationError(`Bitte gib einen Preis an`, { key, target });
  })
  @Field(() => Float)
  price!: number;

  @validate(({ value, key, target }) => {
    if (!value) throw new ValidationError(`Bitte gib einen Bestell-Link an`, { key, target });
    if (!/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}[/]?/.test(value))
      throw new ValidationError('Bitte gib den Link komplett an (z.B.: https://google.com)', { key, target });
  })
  @Field(() => String)
  link!: string;

  @validate(({ value, key, target }) => {
    if (target.price >= 100 && !value) throw new ValidationError('Bitte gib eine Begründung an', { key, target });
  })
  @Field(() => String)
  explanation?: string;

  @Field(() => Boolean, { default: v => v ?? false })
  acceptAlternative!: boolean;

  @validate(({ value, key, target }) => {
    if (target.price >= 100 && !value)
      throw new ValidationError('Bitte gib einen Verwendungszweck an', { key, target });
  })
  @Field(() => String)
  purpose?: string;

  @Field(() => String)
  public productCategory: LogistikProductCategory = LogistikProductCategory.INDIVIDUAL;
}

@Entity()
export class LogistikEntireTrip extends AdornisEntity {
  static override _class = 'LogistikEntireTrip';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(() => String)
  description: Maybe<string>;

  @Field(() => String, { default: v => v ?? LogistikProductBahncard.NO_BAHNCARD })
  bahncard!: LogistikProductBahncard;

  @validate(({ value, key, target }) => {
    if (!value) throw new ValidationError(`Es muss das Alter bei Fahrtanrtitt angegeben werden.`, { key, target });
    if (Object.values(LogistikTripAge).indexOf(value) === -1)
      throw new ValidationError(`Ungültiges Alter bei Fahrtantritt.`, { key, target });
  })
  @Field(() => String)
  ageAtDeparture: Maybe<LogistikTripAge>;

  @Field(() => String)
  reason: Maybe<string>;

  @Field(() => String)
  public productCategory: LogistikProductCategory = LogistikProductCategory.ENTIRE_TRIP;
}

@Entity()
export class LogistikOtherOrder extends AdornisEntity {
  static override _class = 'LogistikOtherOrder';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(() => String)
  description: Maybe<string>;

  @Field(() => String)
  public productCategory: LogistikProductCategory = LogistikProductCategory.OTHER;
}

export const validateCustomerEmail = (
  emailAddress: string,
  isManuallyCreated: boolean,
  shippingDetails: LogistikOrderShippingDetails,
) => {
  if (projectConfig.get('SERVICE_NAME') === 'chancenhefte') return;
  if (
    !isManuallyCreated &&
    !emailAddress.endsWith('@chancenwerk.de') &&
    !emailAddress.endsWith('@chancenwerk.org') &&
    !emailAddress.endsWith('@adornis.de')
  )
    throw new ValidationError(`Nur E-Mail Adressen des Chancenwerks sind erlaubt.`, {
      key: 'customerEmail',
      target: shippingDetails,
      source: shippingDetails,
    });
};

@Entity()
export class LogistikOrderShippingDetails extends AdornisEntity {
  public static override _class = 'HubOrderShippingDetails'; // TODO needs migration before we can change this

  @validate(({ value, key, source, target }) => {
    if (!value) throw new ValidationError(`Es muss eine E-Mail Adresse angegeben werden.`, { key, source, target });
    emailValidation({ value, key, target });
  })
  @Field(() => String)
  public customerEmail!: string;

  @Field(() => String, { default: v => v ?? LogistikShippingRecipient.SCHOOL })
  public shippingRecipient!: LogistikShippingRecipient;

  @Field(() => String)
  public shippingRecipientName!: string;

  @Field(() => String)
  shippingRecipientzuHaenden: Maybe<string>;

  @validate(({ value, key, target }) => {
    if (target.shippingRecipient !== LogistikShippingRecipient.SCHUKO) return;
    const validationErrors: ValidationError[] = [];
    if (!target.shippingAddress?.strasse)
      validationErrors.push(
        new ValidationError('Bitte gib eine Straße ein', { path: ['shippingAddress'], key: 'strasse', target }),
      );
    if (!target.shippingAddress?.zipCity?.zip || !target.shippingAddress.zipCity.city)
      validationErrors.push(
        new ValidationError('Bitte gib eine PLZ und Stadt ein', { path: ['shippingAddress'], key: 'zipCity', target }),
      );

    if (validationErrors.length) throw new MultipleValidationError(validationErrors);
  })
  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  public shippingAddress: Maybe<Adresse>;

  @validate(({ value, key, target }) => {
    if (!value) throw new ValidationError(`Es muss ein Standort angegeben werden.`, { key, target });
  })
  @Field(() => String)
  public shippingSchoolID!: string;

  @Field(() => String)
  public kpfID: Maybe<string>;

  // this fetches every partner-type (e.g. OfficePartner) as long as the id is in 'shippingSchoolID' and it's located in the Partner collection
  @Field(() => Partner, {
    resolve: function (this: LogistikOrderShippingDetails) {
      return async (gqlFields: BaseQLSelectionSet<Partner>) =>
        // need to use this helper to get public schools instead of dataloader because publish filter wouldn't allow for any partner when using dataloader
        PartnerPublicInformationAPI.getByID([this.shippingSchoolID])(gqlFields).then(result => result[0]);
    },
  })
  public shippingSchool: Maybe<Partner>;

  @Field(() => String)
  public shippingInstructions!: string;

  @Field(() => DateTime, { default: v => v ?? DateTime.local().plus({ days: 7 }) })
  public dueDate!: DateTime;

  @Field(() => Boolean, {
    resolve: function (this: LogistikOrderShippingDetails) {
      return async () => {
        const user = await (
          await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName)
        ).findOne<ChancenwerkUser>(
          { email: { $regex: `^${this.customerEmail.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } },
          { projection: { _id: 1, _class: 1, email: 1 } },
        );

        if (!user) return false;
        const VerwaltungsMitarbeiterClass =
          await import('./master-data-management/users/entities/VerwaltungsMitarbeiter.js');
        if (
          user._class === Mitarbeiter._class ||
          (user._class === ExternerMitarbeiter._class && user.email?.endsWith('chancenwerk.org')) ||
          user._class === VerwaltungsMitarbeiterClass.VerwaltungsMitarbeiter._class
        )
          return true;
        return false;
      };
    },
  })
  public isEmailKnown!: boolean;

  @Field(() => KPF, {
    resolve: function (this: LogistikOrderShippingDetails) {
      return async (gqlFields: BaseQLSelectionSet<KPF<any>>) => {
        if (!this.kpfID) return null;
        return KPFPublicInformationAPI.getByID([this.kpfID])(gqlFields).then(kpfs => kpfs[0]);
      };
    },
  })
  kpf: Maybe<KPF<any>>;
}

export const displayLogistikOrderID = (entityInstance: LogistikOrder | EntityData<LogistikOrder>) => {
  if (!entityInstance.splitOrderIDs?.length && !entityInstance.splitFrom) {
    return entityInstance.orderNumber === '' ? 'nicht vorhanden' : entityInstance.orderNumber;
  } else {
    if (entityInstance.splitOrderIDs?.length) {
      return `${entityInstance.orderNumber} #1`;
    }
    if (entityInstance.splitFrom) {
      return `${entityInstance.orderNumber} #${entityInstance.splitIndex ? entityInstance.splitIndex + 2 : 2}`;
    }
  }
};
